import type { IntercomReplaySuiteBuilt } from '@sbc/domain-models/agents';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgents } from '@/lib/api/agents';
import { ApiError } from '@/lib/api/client';
import { useBuildIntercomReplaySuite } from '@/lib/api/eval-suites';

const DEFAULT_SUITE = 'intercom-drafter-replay';

export function ReplaySuiteBuilder() {
  const agentsQuery = useAgents({ page: 1, pageSize: 100 });
  const agents = agentsQuery.data?.items ?? [];
  const build = useBuildIntercomReplaySuite();
  const [agentId, setAgentId] = useState('');
  const [cases, setCases] = useState('200');
  const [days, setDays] = useState('21');
  const [result, setResult] = useState<IntercomReplaySuiteBuilt | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onBuild = () => {
    setError(null);
    setResult(null);
    build.mutate(
      {
        suiteName: DEFAULT_SUITE,
        agentId,
        cases: Number.parseInt(cases, 10) || 200,
        lookbackDays: Number.parseInt(days, 10) || 21,
      },
      {
        onSuccess: setResult,
        onError: (caught) =>
          setError(caught instanceof ApiError ? caught.message : 'Could not build the suite.'),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Intercom drafter replay</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Builds (or rebuilds) the <code>{DEFAULT_SUITE}</code> suite from recent graded shadow
          drafts: each case replays the conversation with the account facts recorded at draft time
          and is graded against the CS reply.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-2 sm:col-span-1">
            <Label htmlFor="replayAgent">Drafter agent</Label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger id="replayAgent">
                <SelectValue placeholder={agentsQuery.isPending ? 'Loading…' : 'Select'} />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.activeVersion.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="replayCases">Cases</Label>
            <Input
              id="replayCases"
              type="number"
              min={1}
              max={500}
              value={cases}
              onChange={(event) => setCases(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="replayDays">Look back (days)</Label>
            <Input
              id="replayDays"
              type="number"
              min={1}
              max={90}
              value={days}
              onChange={(event) => setDays(event.target.value)}
            />
          </div>
        </div>
        <Button onClick={onBuild} disabled={!agentId || build.isPending}>
          {build.isPending ? 'Building… (reads Intercom, about a minute)' : 'Build replay suite'}
        </Button>
        {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
        {result && (
          <p className="text-sm">
            {result.taskCount} cases saved ({result.skipped} skipped):{' '}
            {result.byCategory.map((entry) => `${entry.category} ${entry.count}`).join(', ')}.{' '}
            <Link
              to="/evals/suites/$suiteId"
              params={{ suiteId: result.suiteId }}
              className="hover:underline"
            >
              Open suite →
            </Link>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
